"use client"

import Link from "next/link"
import type { ReactNode } from "react"
import RiArrowDownSLine from "~/icons/remix/generated/RiArrowDownSLine"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu"
import { FRAMEWORKS_MENU, type FrameworksMenuItem } from "~/lib/navigation/frameworks-menu"
import { cx } from "~/lib/utils"

/**
 * Desktop "Frameworks" nav dropdown. Radix portals the panel to <body>, so
 * it escapes the nav pill's `overflow-hidden` clip (a CSS-only dropdown
 * would be cut off). Each row is icon + name + one-line descriptor — the
 * "snippet under each link" pattern that disambiguates the two products and
 * their skills tiers at a glance. The "Overview" row is the family hub, so
 * the trigger is both a menu and a path to the hub.
 */
export function FrameworksNavMenu({
  onNavigate,
}: {
  /** Fires on item click for nav analytics (same handler the flat links use). */
  onNavigate: (linkName: string, href: string) => void
}): ReactNode {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="group flex items-center gap-1 whitespace-nowrap px-2 py-1 font-medium text-gray-900 outline-none focus-visible:ring-2 focus-visible:ring-primary/50 data-[state=open]:text-primary dark:text-gray-50"
        aria-label="Frameworks menu"
      >
        Frameworks
        <RiArrowDownSLine
          aria-hidden
          className="size-4 transition-transform duration-200 group-data-[state=open]:rotate-180"
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="center"
        sideOffset={14}
        className="w-[19rem] rounded-xl border-gray-100 bg-white/95 p-2 shadow-xl shadow-black/10 backdrop-blur-nav dark:border-white/10 dark:bg-gray-900/95"
      >
        {FRAMEWORKS_MENU.map((item, i) => {
          // Separate the skills-only tier from the full frameworks with a rule.
          const prev = FRAMEWORKS_MENU[i - 1]
          const needsSeparator = prev && prev.group !== "skills" && item.group === "skills"
          return (
            <div key={item.href}>
              {needsSeparator ? <DropdownMenuSeparator /> : null}
              <DropdownMenuItem asChild className="cursor-pointer rounded-lg p-0">
                <Link
                  href={item.href}
                  prefetch={false}
                  onClick={() => onNavigate(item.name, item.href)}
                  className="flex items-start gap-3 px-2.5 py-2"
                >
                  <span
                    className={cx(
                      "mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg",
                      item.group === "skills"
                        ? "bg-amber-500/10 text-amber-500"
                        : "bg-primary/10 text-primary",
                    )}
                  >
                    <MenuIcon name={item.icon} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-gray-900 dark:text-gray-50">
                      {item.name}
                    </span>
                    <span className="block text-xs leading-snug text-gray-500 dark:text-gray-400">
                      {item.description}
                    </span>
                  </span>
                </Link>
              </DropdownMenuItem>
            </div>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function MenuIcon({ name }: { name: FrameworksMenuItem["icon"] }): ReactNode {
  const common = { viewBox: "0 0 20 20", fill: "none", stroke: "currentColor", strokeWidth: 1.6, "aria-hidden": true, className: "size-4" } as const
  switch (name) {
    case "grid":
      return (
        <svg {...common}>
          <rect x="2.5" y="2.5" width="6" height="6" rx="1.2" />
          <rect x="11.5" y="2.5" width="6" height="6" rx="1.2" />
          <rect x="2.5" y="11.5" width="6" height="6" rx="1.2" />
          <rect x="11.5" y="11.5" width="6" height="6" rx="1.2" />
        </svg>
      )
    case "film":
      return (
        <svg {...common}>
          <rect x="2.5" y="3.5" width="15" height="13" rx="1.6" />
          <path d="M2.5 7.5h15M2.5 12.5h15M6.5 3.5v13M13.5 3.5v13" />
        </svg>
      )
    case "mail":
      return (
        <svg {...common}>
          <rect x="2.5" y="4" width="15" height="12" rx="1.6" />
          <path d="m3 5 7 5 7-5" />
        </svg>
      )
    case "sparkles":
      return (
        <svg {...common}>
          <path d="M10 2.5 11.4 7l4.5 1.4-4.5 1.4L10 14.3 8.6 9.8 4.1 8.4 8.6 7 10 2.5Z" />
          <path d="M15.5 13.5l.6 1.9 1.9.6-1.9.6-.6 1.9-.6-1.9-1.9-.6 1.9-.6.6-1.9Z" />
        </svg>
      )
  }
}
